<script>
  import ButtonContainer from "@helpers/ButtonContainer.svelte";
  import { flip } from "svelte/animate";

  const baseBudgetBlocks = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
  let budget = {
    index: -1,
    name: "Budget Blocks",
    items: $budgetBlocks.length > 0 ? [] : baseBudgetBlocks,
  };

  // Create an array with four objects. One for each department in $topFourDepartments. Each object should have an index, name, and items array.
  let departments =
    $budgetBlocks.length > 0
      ? $budgetBlocks
      : $topFourDepartments.map((d, index) => ({
          index: index,
          name: d.Department,
          items: [],
        }));

  import deepLookup from "@helpers/deepLookup.js";

  // When the array or any of its individual values change, reset topFourDepartmentsArchive and reset budget blocks
  let topFourDepartmentsArchive = $topFourDepartments;
  $: if (deepLookup($topFourDepartments, topFourDepartmentsArchive))
    resetBlocks();

  const resetBlocks = function () {
    // Resetting due to a change in topFourDepartments above
    topFourDepartmentsArchive = $topFourDepartments;
    departments = $topFourDepartments.map((d, index) => ({
      index: index,
      name: d.Department,
      items: [],
    }));
    budget.items = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
  };

  // Submissions
  $: canSubmit = false;
  $: {
    if (budget.items.length == 0) {
      canSubmit = true;
    } else {
      canSubmit = false;
    }
  }

  import { budgetBlocks, topFourDepartments } from "@stores/userData.js";
  let buttonText = "Submit";

  import navigateToHash from "@helpers/navigateToHash.js";
  const handleSubmit = function () {
    buttonText = "Thanks!";
    budgetBlocks.set(departments);

    navigateToHash("context", 500);
  };

  let hoveringOver;

  /**
   * @param event
   * @param basketIndex - the index of the basket from which the item is leaving
   * @param itemIndex - the index of the item being dragged
   */
  function dragStart(event, basketIndex, itemIndex) {
    const data = { basketIndex, itemIndex };
    event.dataTransfer.setData("text/plain", JSON.stringify(data));
  }

  /**
   * @param event
   * @param basketIndex - the index of the basket to which the item is being dropped
   */
  function drop(event, basketIndex) {
    event.preventDefault();
    buttonText = "Submit"; // If user edits submission after submitting, reset button text

    const json = event.dataTransfer.getData("text/plain");
    const data = JSON.parse(json);

    // Remove the item from the previous basket.
    // (Splice returns an array of the deleted elements, just one in this case.)
    let item;
    if (data.basketIndex == -1) {
      item = budget.items.splice(data.itemIndex, 1);
    } else {
      item = departments[data.basketIndex].items.splice(data.itemIndex, 1);
    }

    // Add the item to the target basket.
    if (basketIndex == -1) {
      budget.items.push(item);
    } else {
      departments[basketIndex].items.push(item);
    }

    budget = budget;
    departments = departments;
    hoveringOver = null;
  }
</script>

<div class="dropzones">
  <div class="budget">
    <span class="dropzone-label">{budget.name} ({budget.items.length}/10)</span>
    <ul
      class:hovering={hoveringOver === budget.name}
      on:dragenter={() => (hoveringOver = budget.name)}
      on:dragleave={() => (hoveringOver = null)}
      on:drop={(event) => drop(event, budget.index)}
      ondragover="return false"
    >
      {#each budget.items as item, itemIndex (item)}
        <li class="item" animate:flip>
          <img
            src="/images/emojis/money.png"
            alt="Dollar bill"
            draggable={true}
            on:dragstart={(event) => dragStart(event, budget.index, itemIndex)}
          />
        </li>
      {/each}
    </ul>
  </div>
  <div class="departments">
    {#each departments as department}
      <div class="department">
        <span class="dropzone-label-small">
          <button
            class="num-toggle decrement"
            class:disabled={department.items.length == 0}
            on:click={() => {
              // Take the last item in the department and move it to the budget.
              if (department.items.length > 0) {
                budget.items.push(department.items.pop());
                budget = budget;
                departments = departments;
              }
            }}>&#8722;</button
          >
          {department.name}
          <button
            class="num-toggle increment"
            class:disabled={budget.items.length == 0}
            on:click={() => {
              // Take the last item in budget.items and place it in department.items
              if (budget.items.length > 0) {
                department.items.push(budget.items.pop());
                budget = budget;
                departments = departments;
              }
            }}
            >&#43;
          </button>
        </span>
        <ul
          class:hovering={hoveringOver === department.name}
          on:dragenter={() => (hoveringOver = department.name)}
          on:dragleave={() => (hoveringOver = null)}
          on:drop={(event) => drop(event, department.index)}
          ondragover="return false"
        >
          {#each department.items as item, itemIndex (item)}
            <li class="item" animate:flip>
              <img
                src="/images/emojis/money.png"
                alt="Dollar bill"
                draggable={true}
                on:dragstart={(event) =>
                  dragStart(event, department.index, itemIndex)}
              />
            </li>
          {/each}
        </ul>
      </div>
    {/each}
  </div>
</div>
<ButtonContainer
  disabled={!canSubmit}
  disabledReason={`${10 - budget.items.length} out of 10 distributed!`}
>
  <button
    disabled={!canSubmit}
    class:disabled={!canSubmit}
    on:click={handleSubmit}>{buttonText}</button
  >
</ButtonContainer>

<style>
  .dropzones {
    margin: 2rem 0;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 1068px;
  }

  .budget,
  .departments {
    position: relative;
  }

  .budget {
    flex: 1;
    margin-bottom: 1rem;
  }

  .budget,
  .department {
    position: relative;
  }

  .budget {
    margin-bottom: 1rem;
  }

  .departments {
    height: 100%;
  }

  .department {
    height: 75px;
  }

  /* DEPARTMENTS DROPZONES */
  .department ul {
    display: flex;
    flex-direction: row;
    place-items: flex-start;
    justify-content: flex-end;
    padding: 1rem;
    overflow: hidden;
  }

  .department ul li img {
    max-height: 40px;
  }

  .department:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.4);
  }

  /* LABELS PER BOX */
  .dropzone-label {
    position: absolute;
    bottom: 0;
    right: 0;
    font-size: 1.75rem;
    color: rgba(var(--text-color-rgb), 0.8);
    font-family: var(--font-serif);
    padding: 0.75rem;
    user-select: none;
    -webkit-user-select: none; /* Chrome/Safari */
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* IE10+ */
    /* background: #f5f5ff;
    background: linear-gradient(
      to bottom,
      rgba(245, 245, 255, 0) 0%,
      rgb(245, 245, 255) 30%
    ); */
    background: white;
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0) 0%,
      rgb(255, 255, 255) 30%
    );
    z-index: 100;
  }

  .dropzone-label-small {
    position: absolute;
    bottom: 0;
    left: 0;
    font-size: 1.5rem;
    pointer-events: none;
    font-weight: 300;
    padding: 0.45rem;
    user-select: none;
    -webkit-user-select: none; /* Chrome/Safari */
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* IE10+ */
    text-align: left;
    z-index: 10;
  }

  /* BLOCKS */
  .hovering {
    border-color: var(--accent-color);
  }

  li {
    display: inline-block;
    user-select: none;
    -webkit-user-select: none; /* Chrome/Safari */
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* IE10+ */
  }

  li img {
    /* background-color: lightgray; */
    /* padding: 10px; */
    /* box-shadow: 1px 1px 4px rgba(var(--text-color-rgb), 0.5); */
    cursor: pointer;
    display: inline-block;
    margin-right: 10px;
    margin-bottom: 10px;
    user-select: none;
    -webkit-user-select: none; /* Chrome/Safari */
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* IE10+ */
    max-height: 60px;
  }

  .budget ul {
    flex-wrap: wrap;
    justify-content: flex-start;
    place-items: flex-start;
    align-content: flex-start;
    overflow: hidden;
    height: 100px;
  }

  ul {
    /* background: rgba(var(--primary-color-rgb), 0.8); */
    background: white;
    box-shadow: 1px 1px 4px rgba(var(--text-color-rgb), 0.5);
    display: flex;
    height: 100%;
    padding: 1rem;
  }

  .num-toggle {
    font-size: 2rem;
    font-family: var(--font-sans);
    color: rgba(var(--text-color-rgb), 1);
    cursor: pointer;
    transition: all 500ms ease;
    pointer-events: all;
  }

  .num-toggle.disabled {
    opacity: 0.25;
    cursor: not-allowed;
  }

  @media screen and (max-width: 768px) {
    .dropzones {
      flex-direction: column;
      height: auto;
    }
    .budget {
      margin: 0;
      min-height: 220px;
    }

    .budget ul {
      height: 220px;
    }
    .departments {
      margin-left: 0;
      margin-top: 1rem;
    }

    .num-toggle {
      font-size: 1.25rem;
    }
  }

  @media screen and (max-width: 568px) {
    .budget {
      min-height: 180px;
    }
    .budget ul {
      height: 180px;
    }
    .dropzone-label-small {
      font-size: 1rem;
    }

    .department ul li img {
      max-height: 30px;
    }
  }
</style>
